import { ProfileAbilityBuilder, UpdateAbilityContext } from '../types'
import { ACTIONS, ROLES, SUBJECTS } from 'features/Permission/constants'
import { isHospitalFamilyUser } from './utils'
import { isInsideCommunityCondition } from '../conditions'

const grantCognusRepositoryAbilities = ({ can }: ProfileAbilityBuilder) => {
  can(ACTIONS.VIEW, SUBJECTS.CENTRAL_REPOSITORY)
  can(ACTIONS.VIEW, SUBJECTS.AVAILABLE_FOR_EVALUATION)
  can(ACTIONS.VIEW, SUBJECTS.OPPORTUNITY_ANALYSIS)
  can(ACTIONS.VIEW, SUBJECTS.REQUESTS_FOR_ANALYSIS, isInsideCommunityCondition)
}

const grantPartialCognusRepositoryAbilities = ({
  can
}: ProfileAbilityBuilder) => {
  can(ACTIONS.VIEW, SUBJECTS.CENTRAL_REPOSITORY)
  can(ACTIONS.VIEW, SUBJECTS.AVAILABLE_FOR_EVALUATION)
}

const grantHospitalFamilyHsDetailsAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
}

const defineCommunityPresidentUpdateAbilities = (
  builder: ProfileAbilityBuilder
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.COMPLIANCE)
  can(ACTIONS.DOWNLOAD, SUBJECTS.QR_CODE)
  can(ACTIONS.VIEW, SUBJECTS.MY_ORGANIZATION_COMMUNITY_HS_TABLE_ACTIONS)
  can(ACTIONS.CRUD, ROLES.VICE_PRESIDENT)
  can(ACTIONS.CRUD, ROLES.CONTRACT_STEWARD)
  can(ACTIONS.CRUD, ROLES.ANALYST)
  can(ACTIONS.CRUD, ROLES.LEGAL)
  can(ACTIONS.CRUD, ROLES.HOSPITAL_USER)
  can(ACTIONS.CRUD, ROLES.HOSPITAL_ADMIN)
  can(ACTIONS.CRUD, ROLES.FINANCE)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEMS)
  can(ACTIONS.VIEW, SUBJECTS.COMMUNITY_USERS)
  can(ACTIONS.CRUD, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.CRUD, SUBJECTS.HOSPITALS_IN_HS_DETAILS)
  can(ACTIONS.VIEW, SUBJECTS.BANK_ENROLLED_IN_HOSPITALS_TABLE)
  can(ACTIONS.ARCHIVE, SUBJECTS.HOSPITALS_IN_HS_DETAILS)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HOSPITAL_DETAILS)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HS_DETAILS)
  can(ACTIONS.EDIT, SUBJECTS.USERS_IN_HS_DETAILS)
  can(ACTIONS.CRUD, SUBJECTS.HOSPITAL)
  can(ACTIONS.CRUD, SUBJECTS.HEALTH_SYSTEM_USERS)
  can(ACTIONS.CRUD, SUBJECTS.HOSPITAL_USERS)
  can(ACTIONS.CRUD, SUBJECTS.RFP)
  can(ACTIONS.CRUD, SUBJECTS.DEPARTMENTS)
  // can(ACTIONS.VIEW, SUBJECTS.PURCHASE_ORDERS)
  can(ACTIONS.VIEW, SUBJECTS.FINANCIALS)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.COMMUNITY_ADMIN_SPLIT_FEE)
  can(ACTIONS.VIEW, SUBJECTS.GPO_COMMUNITY)
  can(ACTIONS.CRUD, SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS)
  can(ACTIONS.ARCHIVE, SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS)
  can(ACTIONS.EDIT, SUBJECTS.USER)
  can(ACTIONS.CREATE, SUBJECTS.VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.VENDOR_USERS)
}

const defineVicePresidentUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
  can(ACTIONS.CREATE, SUBJECTS.CONTRACT_PIPELINE)
  can(ACTIONS.CREATE, SUBJECTS.CONTRACTS)
  can(ACTIONS.CREATE, SUBJECTS.BULK_CONTRACTS)
  can(ACTIONS.EDIT, SUBJECTS.HEALTH_SYSTEM, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.COMPLIANCE)
  can(ACTIONS.DOWNLOAD, SUBJECTS.QR_CODE, isHSOwnerCondition)
  can(
    ACTIONS.VIEW,
    SUBJECTS.MY_ORGANIZATION_COMMUNITY_HS_TABLE_ACTIONS,
    isHSOwnerCondition
  )
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.EDIT, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.VIEW, SUBJECTS.BANK_ENROLLED_IN_HOSPITALS_TABLE)
  can(ACTIONS.CRUD, SUBJECTS.COMMUNITY_ACCESS, isHSOwnerCondition)
  can(ACTIONS.EDIT, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.ARCHIVE, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HOSPITAL_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.EDIT, SUBJECTS.USERS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.CRUD, SUBJECTS.SHARE_LOCAL_AGREEMENTS)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_USERS)
  can(ACTIONS.VIEW, SUBJECTS.GPO_HS)
  can(ACTIONS.VIEW, SUBJECTS.COMMUNITY_ADMIN_SPLIT_FEE)
  can(ACTIONS.MANAGE, SUBJECTS.GPO_HS)
  can(ACTIONS.CRUD, ROLES.CONTRACT_STEWARD)
  can(ACTIONS.CRUD, ROLES.ANALYST)
  can(ACTIONS.CRUD, ROLES.LEGAL)
  can(ACTIONS.CRUD, ROLES.FINANCE)
  can(ACTIONS.CRUD, SUBJECTS.HOSPITAL)
  can(ACTIONS.CRUD, SUBJECTS.HOSPITAL_USERS)
  can(ACTIONS.CRUD, SUBJECTS.RFP)
  can(ACTIONS.CRUD, SUBJECTS.DEPARTMENTS)
  // can(ACTIONS.UPLOAD, SUBJECTS.PURCHASE_ORDERS)
  // can(ACTIONS.VIEW, SUBJECTS.PURCHASE_ORDERS)
  // TODO: temporarrily disabled - https://leverxeu.atlassian.net/browse/ECOG01-11520
  // can(ACTIONS.UPLOAD, SUBJECTS.INVOICE_DATA)
  // can(ACTIONS.VIEW, SUBJECTS.INVOICE_DATA)
  can(ACTIONS.VIEW, SUBJECTS.FINANCIALS)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.CONTRACT_PIPELINE)
  can(ACTIONS.CRUD, SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS)
  can(ACTIONS.ARCHIVE, SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS)
  can(ACTIONS.EDIT, SUBJECTS.USER)
  can(ACTIONS.CRUD, SUBJECTS.HEALTH_SYSTEM_USERS)
  can(ACTIONS.CREATE, SUBJECTS.VENDORS)
  can(ACTIONS.ADD, SUBJECTS.MY_VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.VENDOR_USERS)
  grantCognusRepositoryAbilities(builder)
}

const defineContractStewardUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.CREATE, SUBJECTS.CONTRACT_PIPELINE)
  can(ACTIONS.CREATE, SUBJECTS.CONTRACTS)
  can(ACTIONS.CREATE, SUBJECTS.BULK_CONTRACTS)
  can(ACTIONS.VIEW, SUBJECTS.COMMUNITY_ADMIN_SPLIT_FEE)
  // TODO: temporarrily disabled - https://leverxeu.atlassian.net/browse/ECOG01-11520
  // can(ACTIONS.VIEW, SUBJECTS.INVOICE_DATA)
  // can(ACTIONS.UPLOAD, SUBJECTS.INVOICE_DATA)
  can(ACTIONS.DOWNLOAD, SUBJECTS.QR_CODE, isHSOwnerCondition)
  can(
    ACTIONS.VIEW,
    SUBJECTS.MY_ORGANIZATION_COMMUNITY_HS_TABLE_ACTIONS,
    isHSOwnerCondition
  )
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.CRUD, ROLES.ANALYST)
  can(ACTIONS.CRUD, ROLES.LEGAL)
  can(ACTIONS.CRUD, SUBJECTS.HOSPITAL_USERS)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_USERS)
  can(ACTIONS.CRUD, SUBJECTS.RFP)
  can(ACTIONS.UPLOAD, SUBJECTS.PURCHASE_ORDERS)
  can(ACTIONS.VIEW, SUBJECTS.PURCHASE_ORDERS)
  can(ACTIONS.VIEW, SUBJECTS.FINANCIALS)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.CONTRACT_PIPELINE)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.EDIT, SUBJECTS.USERS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HOSPITAL_DETAILS, isHSOwnerCondition)
  can(
    ACTIONS.CRUD,
    SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS,
    isHSOwnerCondition
  )
  can(
    ACTIONS.ARCHIVE,
    SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS,
    isHSOwnerCondition
  )
  can(ACTIONS.EDIT, SUBJECTS.USER)
  can(ACTIONS.CRUD, SUBJECTS.HEALTH_SYSTEM_USERS)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.CREATE, SUBJECTS.VENDORS)
  can(ACTIONS.ADD, SUBJECTS.MY_VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.VENDOR_USERS)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
  grantCognusRepositoryAbilities(builder)
}

const defineHospitalUserUpdateAbilities = (builder: ProfileAbilityBuilder) => {
  const { can, cannot } = builder

  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.CLINICAL_REVIEW)
  can(ACTIONS.CREATE, SUBJECTS.VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.ONLY_GUEST_VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.VENDOR_USERS)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK_HS)
  cannot(
    ACTIONS.VIEW,
    SUBJECTS.QUICK_NAVIGATION_BLOCK_HS,
    isInsideCommunityCondition
  )
}

const defineCommunityRcaUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_USERS)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  // TODO: temporarrily disabled - https://leverxeu.atlassian.net/browse/ECOG01-11520
  // can(ACTIONS.VIEW, SUBJECTS.INVOICE_DATA)
  // can(ACTIONS.UPLOAD, SUBJECTS.INVOICE_DATA)
  can(ACTIONS.VIEW, SUBJECTS.FINANCIALS)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
}

const defineLegalUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.CRUD, ROLES.LEGAL)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
}

const defineAnalystUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.CRUD, ROLES.LEGAL)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.UPLOAD, SUBJECTS.PURCHASE_ORDERS)
  can(ACTIONS.VIEW, SUBJECTS.PURCHASE_ORDERS)
  // TODO: temporarrily disabled - https://leverxeu.atlassian.net/browse/ECOG01-11520
  // can(ACTIONS.VIEW, SUBJECTS.INVOICE_DATA)
  // can(ACTIONS.UPLOAD, SUBJECTS.INVOICE_DATA)
  can(ACTIONS.VIEW, SUBJECTS.FINANCIALS)
  can(ACTIONS.CREATE, SUBJECTS.VENDORS)
  can(ACTIONS.ADD, SUBJECTS.MY_VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.VENDOR_USERS)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
  grantCognusRepositoryAbilities(builder)
}

const defineClinicalCoordinatorUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.VIEW, SUBJECTS.COMPLIANCE)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.CREATE, SUBJECTS.VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.ONLY_GUEST_VENDORS)
  can(ACTIONS.ADD, SUBJECTS.MY_VENDORS)
  can(ACTIONS.CREATE, SUBJECTS.VENDOR_USERS)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
  grantPartialCognusRepositoryAbilities(builder)
}

const defineHospitalAdminUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition, isHospitalOwnerCondition }: UpdateAbilityContext
) => {
  const { can, cannot } = builder
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.VIEW, SUBJECTS.PRODUCT_REQUESTS)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.EDIT, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHospitalOwnerCondition)
  can(
    ACTIONS.VIEW,
    SUBJECTS.USERS_IN_HOSPITAL_DETAILS,
    isHospitalOwnerCondition
  )
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS)
  can(
    ACTIONS.CRUD,
    SUBJECTS.MY_COMMUNITY_HS_DETAILS_HOSPITAL_DETAILS_USERS,
    isHospitalOwnerCondition
  )
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK_HS)
  cannot(
    ACTIONS.VIEW,
    SUBJECTS.QUICK_NAVIGATION_BLOCK_HS,
    isInsideCommunityCondition
  )
}

const defineCommunityFinanceUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { isHSOwnerCondition }: UpdateAbilityContext
) => {
  const { can } = builder

  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITALS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.HOSPITAL_LEGAL_NAME, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.USERS_IN_HOSPITAL_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.FINANCIALS)
  can(ACTIONS.VIEW, SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, isHSOwnerCondition)
  can(ACTIONS.VIEW, SUBJECTS.QUICK_NAVIGATION_BLOCK)
}

const defineCommonCommunityUserUpdateAbilities = (
  builder: ProfileAbilityBuilder,
  { user }: UpdateAbilityContext,
  hospitalFamily: boolean
) => {
  const { can } = builder

  if (!hospitalFamily && user.role !== ROLES.LEGAL) {
    can(ACTIONS.VIEW, SUBJECTS.DASHBOARD)
    if (
      user.role !== ROLES.COMMUNITY_PRESIDENT &&
      user.role !== ROLES.CLINICAL_COORDINATOR &&
      user.role !== ROLES.FINANCE
    ) {
      can(ACTIONS.MANAGE, SUBJECTS.ADDITIONAL_VALUES)
    }
  }

  if (user.role !== ROLES.HOSPITAL_ADMIN && user.role !== ROLES.LEGAL) {
    can(ACTIONS.VIEW, SUBJECTS.VENDORS)
  }

  if (
    !hospitalFamily &&
    user.role !== ROLES.LEGAL &&
    user.role !== ROLES.FINANCE &&
    user.role !== ROLES.CLINICAL_COORDINATOR
  ) {
    can(ACTIONS.VIEW, SUBJECTS.COMPLIANCE)
  }
}

export const updateCommunityAbilities = (
  builder: ProfileAbilityBuilder,
  ctx: UpdateAbilityContext
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.MY_COMMUNITY)
  builder.can(ACTIONS.VIEW, SUBJECTS.CALENDAR)
  builder.can(ACTIONS.VIEW, SUBJECTS.CHAT)

  const { user } = ctx
  const hospitalFamily = isHospitalFamilyUser(user)

  if (user.role !== ROLES.COMMUNITY_RCA) {
    builder.can(ACTIONS.VIEW, SUBJECTS.CONTRACTS)

    if (user.role !== ROLES.FINANCE) {
      builder.can(ACTIONS.VIEW, SUBJECTS.BIDDING)
    }
  }

  switch (user.role) {
    case ROLES.COMMUNITY_PRESIDENT:
      defineCommunityPresidentUpdateAbilities(builder)
      break
    case ROLES.VICE_PRESIDENT:
      defineVicePresidentUpdateAbilities(builder, ctx)
      break
    case ROLES.CONTRACT_STEWARD:
      defineContractStewardUpdateAbilities(builder, ctx)
      break
    case ROLES.HOSPITAL_USER:
      defineHospitalUserUpdateAbilities(builder)
      break
    case ROLES.COMMUNITY_RCA:
      defineCommunityRcaUpdateAbilities(builder, ctx)
      break
    case ROLES.LEGAL:
      defineLegalUpdateAbilities(builder, ctx)
      break
    case ROLES.ANALYST:
      defineAnalystUpdateAbilities(builder, ctx)
      break
    case ROLES.CLINICAL_COORDINATOR:
      defineClinicalCoordinatorUpdateAbilities(builder, ctx)
      break
    case ROLES.HOSPITAL_ADMIN:
      defineHospitalAdminUpdateAbilities(builder, ctx)
      break
    case ROLES.FINANCE:
      defineCommunityFinanceUpdateAbilities(builder, ctx)
      break
  }

  if (hospitalFamily) {
    grantHospitalFamilyHsDetailsAbilities(builder, ctx)
  }

  if (!hospitalFamily) {
    builder.can(ACTIONS.VIEW, SUBJECTS.RESPONSIBLE_USER)
  }

  if (user.role !== ROLES.COMMUNITY_RCA) {
    defineCommonCommunityUserUpdateAbilities(builder, ctx, hospitalFamily)
  }
}
